import { useState, useEffect, useRef } from 'react'
import { MessageCircle, X, ChevronLeft, Send } from 'lucide-react'

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FAQ_DATA = [
  {
    id: 'tipo-cambio',
    icon: '💵',
    question: '¿Cuál es el tipo de cambio hoy?',
    answer: null, // Se llena dinámicamente
    dynamic: true
  },
  {
    id: 'ubicacion',
    icon: '📍',
    question: '¿Dónde están ubicados?',
    answer: 'Contamos con dos sucursales en Mexicali, B.C.:\n\n📍 **Sucursal 1:** Calz. Justo Sierra 211-A, Nueva, 21100 Mexicali, B.C.\n\n📍 **Sucursal 2:** Calz. Justo Sierra 18, Nueva, 21100 Mexicali, B.C.\n\n¡Te esperamos en cualquiera de nuestras ubicaciones!'
  },
  {
    id: 'horario',
    icon: '🕐',
    question: '¿Cuál es su horario?',
    answer: 'Nuestros horarios varían por sucursal:\n\n📍 **Sucursal Principal:**\n🗓️ Lunes a Sábado: 7:00 AM - 7:00 PM\n\n📍 **Segunda Sucursal (Justo Sierra 18):**\n🗓️ Lunes a Sábado: 7:00 AM - 11:00 PM\n🗓️ Domingo: 7:00 AM - 7:00 PM\n\n¡Te esperamos!'
  },
  {
    id: 'contacto',
    icon: '📞',
    question: '¿Cómo los contacto?',
    answer: 'Puedes contactarnos de las siguientes formas:\n\n📞 **Teléfono:** 686 568 13 00\n\n📍 **Visítanos** en cualquiera de nuestras dos sucursales en Mexicali.\n\n¡Con gusto te atendemos!'
  },
  {
    id: 'divisas',
    icon: '💱',
    question: '¿Qué divisas manejan?',
    answer: 'Nuestra especialidad es el cambio directo entre:\n\n🇺🇸 **Dólares (USD)**\n🇲🇽 **Pesos Mexicanos (MXN)**\n\n¡Siempre garantizando el mejor tipo de cambio del mercado!'
  },
  {
    id: 'documentos',
    icon: '📄',
    question: '¿Qué documentos necesito?',
    answer: 'Los documentos requeridos dependen del monto de la operación:\n\n📋 **Todos los niveles requieren:** INE/IFE vigente.\n\n💵 **$1,000 - $2,999.99 USD**\nINE + registro de datos en el sistema.\n\n💵 **$3,000 - $4,999.99 USD**\nINE + registro en sistema + **copia de ID** (incluyendo No. de identificación, teléfono y ocupación).\n\n💵 **$5,000 USD o más**\nINE + registro + **Declaración Firmada** + documentación según tipo de usuario.\n\n*Regulación CNBV vigente. Más info: 686 568 13 00.*'
  },
  {
    id: 'montos',
    icon: '💰',
    question: '¿Hay monto mínimo o máximo?',
    answer: '¡No hay monto mínimo para realizar tus operaciones!\n\n💡 Para montos **mayores a $5,000 USD**, te recomendamos contactar a un asesor al **686 568 13 00** para brindarte atención personalizada y las mejores condiciones.\n\nPara montos muy grandes podemos ofrecerte tasas preferenciales.'
  },
  {
    id: 'seguridad',
    icon: '🔒',
    question: '¿Es seguro cambiar aquí?',
    answer: '¡Absolutamente! Tu seguridad es nuestra prioridad:\n\n✅ **+20 años** de experiencia en el mercado cambiario.\n\n✅ Cumplimos con **todas las regulaciones financieras** establecidas.\n\n✅ Transacciones **100% seguras** y transparentes.\n\n✅ Somos un centro cambiario **debidamente registrado** ante las autoridades correspondientes.'
  }
]

function formatMessage(text) {
  if (!text) return ''
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>')
}

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [showQuestions, setShowQuestions] = useState(true)
  const [exchangeRate, setExchangeRate] = useState(null)
  const [hasNewMessage, setHasNewMessage] = useState(false)
  const messagesEndRef = useRef(null)

  // Fetch exchange rate for dynamic answer
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const response = await fetch(`${API_URL}/api/rates`)
        if (!response.ok) throw new Error('Error')
        const rates = await response.json()
        setExchangeRate(rates)
      } catch (error) {
        console.error('Error fetching rates for chatbot:', error)
      }
    }
    fetchRate()
    const interval = setInterval(fetchRate, 30000)
    return () => clearInterval(interval)
  }, [])

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  // Show notification dot after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen) setHasNewMessage(true)
    }, 3000)
    return () => clearTimeout(timer)
  }, [])

  const handleOpen = () => {
    setIsOpen(true)
    setHasNewMessage(false)
    if (messages.length === 0) {
      setMessages([{
        type: 'bot',
        text: '¡Hola! 👋 Soy el asistente virtual de **Del Sol Centro Cambiario**. ¿En qué puedo ayudarte?',
        time: new Date()
      }])
    }
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleQuestionClick = (faq) => {
    // Add user message
    const userMsg = {
      type: 'user',
      text: faq.question,
      time: new Date()
    }

    let answer = faq.answer
    if (faq.dynamic && exchangeRate) {
      answer = `El tipo de cambio del **Dólar USD** hoy es:\n\n💰 **Compra:** $${exchangeRate.buy} MXN\n💰 **Venta:** $${exchangeRate.sell} MXN\n\n📅 Actualizado: ${exchangeRate.lastUpdate}\n\nEstos valores se actualizan constantemente. ¡Visítanos para obtener las mejores tasas!`
    } else if (faq.dynamic) {
      answer = 'Lo siento, no pude obtener el tipo de cambio en este momento. Te invito a llamarnos al **686 568 13 00** para consultar la tasa actual.'
    }

    const botMsg = {
      type: 'bot',
      text: answer,
      time: new Date()
    }

    setMessages(prev => [...prev, userMsg])
    setShowQuestions(false)

    // Simulate typing delay
    setTimeout(() => {
      setMessages(prev => [...prev, botMsg])
      // Show questions again after answer
      setTimeout(() => setShowQuestions(true), 500)
    }, 600)
  }

  const handleBack = () => {
    setMessages([{
      type: 'bot',
      text: '¡Hola! 👋 Soy el asistente virtual de **Del Sol Centro Cambiario**. ¿En qué puedo ayudarte?',
      time: new Date()
    }])
    setShowQuestions(true)
  }

  const formatTime = (date) => {
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <>
      {/* Chat Widget */}
      {isOpen && (
        <div className="chatbot-window fade-in">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">
                <img src="/logo192.png" alt="Del Sol" />
              </div>
              <div>
                <div className="chatbot-header-title">Del Sol Asistente</div>
                <div className="chatbot-header-status">
                  <span className="chatbot-status-dot"></span>
                  En línea
                </div>
              </div>
            </div>
            <button className="chatbot-close-btn" onClick={handleClose}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`chatbot-msg chatbot-msg-${msg.type} fade-in`}>
                {msg.type === 'bot' && (
                  <div className="chatbot-msg-avatar">🤖</div>
                )}
                <div className={`chatbot-msg-bubble chatbot-bubble-${msg.type}`}>
                  <div dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }} />
                  <span className="chatbot-msg-time">{formatTime(msg.time)}</span>
                </div>
              </div>
            ))}

            {/* Question Buttons */}
            {showQuestions && messages.length > 0 && (
              <div className="chatbot-questions fade-in">
                <p className="chatbot-questions-label">Selecciona una pregunta:</p>
                <div className="chatbot-questions-grid">
                  {FAQ_DATA.map((faq) => (
                    <button
                      key={faq.id}
                      className="chatbot-question-btn"
                      onClick={() => handleQuestionClick(faq)}
                    >
                      <span className="chatbot-question-icon">{faq.icon}</span>
                      <span className="chatbot-question-text">{faq.question}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Footer */}
          <div className="chatbot-footer">
            {messages.length > 1 && (
              <button className="chatbot-reset-btn" onClick={handleBack}>
                <ChevronLeft className="w-4 h-4" />
                Reiniciar conversación
              </button>
            )}
            <div className="chatbot-footer-note">
              Respuestas automáticas • Del Sol © 2025
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        className={`chatbot-fab ${isOpen ? 'chatbot-fab-active' : ''}`}
        onClick={isOpen ? handleClose : handleOpen}
        aria-label="Abrir chat"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6" />
            {hasNewMessage && <span className="chatbot-fab-badge"></span>}
          </>
        )}
      </button>
    </>
  )
}
